import React, { useEffect, useRef } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { SpinResponse, WheelData, SpinRequest } from '../../../../types';
import SocialShareButtons from './SocialShareButtons';
import { publicApi } from '../../../../services/api';
import moment from 'moment-timezone';
import 'moment/locale/pt-br';

// Configure moment locale
moment.locale('pt-br');

interface ResultScreenProps {
  spinResult: SpinResponse;
  wheelData: WheelData;
  formData: SpinRequest;
  customFieldsData: Record<string, any>;
}

const ResultScreen: React.FC<ResultScreenProps> = ({
  spinResult,
  wheelData,
  formData,
  customFieldsData
}) => {
  const isScheduledDraw = spinResult.instant_draw === false;
  const isNoPrize = spinResult.prize?.is_no_prize;
  const notificationSent = useRef(false);
  
  // Extract slugs from wheel full slug (format: companySlug/wheelSlug)
  const slugParts = wheelData.wheel.slug.split('/');
  const companySlug = slugParts[0] || '';
  const wheelSlug = slugParts[1] || slugParts[0]; // Fallback to full slug if no separator

  // Send WhatsApp notification when result screen is shown (only for instant draws)
  useEffect(() => {
    // Only send once and only if it's not a "no prize" result and not a scheduled draw
    if (!notificationSent.current && !isNoPrize && spinResult.spin_id && !isScheduledDraw) {
      notificationSent.current = true;
      
      publicApi.sendPrizeNotification(spinResult.spin_id).then(result => {
        if (result.sent) {
          console.log('WhatsApp notification sent successfully');
        } else {
          console.log('WhatsApp notification not sent:', result.message);
        }
      }).catch(error => {
        console.error('Error sending WhatsApp notification:', error);
        // Don't show error to user - this is a background operation
      });
    }
  }, [spinResult.spin_id, isNoPrize, isScheduledDraw]);

  // Format draw date for display
  const formatDrawDate = (dateStr: string) => {
    return moment.utc(dateStr).tz('America/Sao_Paulo').format('DD [de] MMMM [de] YYYY [às] HH:mm');
  };

  // Scheduled draw - show participation confirmation
  if (isScheduledDraw) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg p-8 shadow-2xl">
            <div className="mb-6">
              <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Obrigado por Participar!
              </h1>
              <p className="text-gray-600">
                Sua participação foi registrada com sucesso
              </p>
            </div>

            {spinResult.wheel.draw_date && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  📅 Data do Sorteio
                </p>
                <p className="text-lg font-bold text-blue-700">
                  {formatDrawDate(spinResult.wheel.draw_date)}
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  Horário de Brasília
                </p>
              </div>
            )}

            <div className="bg-primary-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-primary-800">
                Aguarde a data do sorteio! Você receberá uma notificação se for o ganhador.
              </p>
            </div>

            {/* Social Share Buttons - Only show if is_shared is true */}
            {wheelData.wheel.is_shared !== false && (
              <div className="mb-6">
                <SocialShareButtons
                  wheelName={wheelData.wheel.name}
                  companySlug={companySlug}
                  wheelSlug={wheelSlug}
                  prizeName="esta promoção"
                  customerName={formData.name}
                  customerWhatsapp={formData.whatsapp}
                />
              </div>
            )}

            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>Nome:</strong> {formData.name}</p>
              <p><strong>WhatsApp:</strong> {formData.whatsapp}</p>
              {formData.birthdate && (
                <p><strong>Data de nascimento:</strong> {formData.birthdate}</p>
              )}
              {customFieldsData && Object.keys(customFieldsData).length > 0 && (
                <div className="pt-2 border-t border-gray-200">
                  {Object.entries(customFieldsData).map(([key, value]) => {
                    const field = wheelData?.wheel.custom_fields?.find(f => f.id === key);
                    if (!field || !value) return null;
                    return (
                      <p key={key}>
                        <strong>{field.label}:</strong> {String(value)}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Boa sorte! 🍀
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Instant draw - show prize result
  return (
    <div className={`min-h-screen bg-gradient-to-br ${isNoPrize ? 'from-orange-600 to-orange-800' : 'from-green-600 to-green-800'} flex items-center justify-center py-12 px-4`}>
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-lg p-8 shadow-2xl">
          <div className="mb-6">
            {isNoPrize ? (
              <>
                <div className="mx-auto h-16 w-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle className="h-8 w-8 text-orange-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Quase lá!
                </h1>
                <p className="text-gray-600">
                  Não foi dessa vez, mas continue tentando!
                </p>
              </>
            ) : (
              <>
                <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Parabéns!
                </h1>
                <p className="text-gray-600">
                  Você ganhou um prêmio!
                </p>
              </>
            )}
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            {spinResult.prize?.image_url && (
              <img
                src={spinResult.prize.image_url}
                alt={spinResult.prize.name}
                className="w-24 h-24 object-cover rounded-lg mx-auto mb-4"
              />
            )}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {spinResult.prize?.name}
            </h2>
          </div>

          {!isNoPrize && (
            <div className="bg-primary-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-primary-800">
                Entraremos em contato pelo WhatsApp para entregar seu prêmio!
              </p>
            </div>
          )}

          {/* Social Share Buttons - Only show if is_shared is true */}
          {spinResult.wheel.is_shared && (
            <div className="mb-6">
              <SocialShareButtons
                wheelName={wheelData.wheel.name}
                companySlug={companySlug}
                wheelSlug={wheelSlug}
                prizeName={spinResult.prize?.name}
                customerName={formData.name}
                customerWhatsapp={formData.whatsapp}
              />
            </div>
          )}

          <div className="space-y-2 text-sm text-gray-600">
            <p><strong>Nome:</strong> {formData.name}</p>
            <p><strong>WhatsApp:</strong> {formData.whatsapp}</p>
            {formData.birthdate && (
              <p><strong>Data de nascimento:</strong> {formData.birthdate}</p>
            )}
            {customFieldsData && Object.keys(customFieldsData).length > 0 && (
              <div className="pt-2 border-t border-gray-200">
                {Object.entries(customFieldsData).map(([key, value]) => {
                  const field = wheelData?.wheel.custom_fields?.find(f => f.id === key);
                  if (!field || !value) return null;
                  return (
                    <p key={key}>
                      <strong>{field.label}:</strong> {String(value)}
                    </p>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Obrigado por participar!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultScreen;
